import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import themeConfig from '../../config/themes/default.json';

/**
 * Branding has three tiers, org override first:
 *   1. org.settings.brand* (per-organization override, set by org admin)
 *   2. platform.brand*     (platform default, set by sultan at runtime)
 *   3. themeConfig         (static fallback from config/themes/default.json)
 *
 * The platform tier is fetched from the public /api/branding/platform
 * endpoint (no auth required) so the login page shows the right brand
 * before the user has a session.
 */

let platformCache = null;           // last fetched values
let platformPromise = null;         // dedupe concurrent fetches
const subscribers = new Set();      // re-render every active hook instance

async function fetchPlatformBranding() {
  if (platformCache) return platformCache;
  if (platformPromise) return platformPromise;
  platformPromise = (async () => {
    try {
      const res = await fetch('/api/branding/platform');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      platformCache = {
        name: data['platform.brandName'] || '',
        tagline: data['platform.brandTagline'] || '',
        logoUrl: data['platform.brandLogoUrl'] || '',
        color: data['platform.brandColor'] || '',
        // Login-page design (platform-only; login is pre-session so no org tier).
        loginDesign: data['platform.loginDesign'] || '',
        loginImageUrl: data['platform.loginImageUrl'] || '',
        loginImagePlacement: data['platform.loginImagePlacement'] || '',
      };
    } catch {
      // Network or 404 — fall back to themeConfig only.
      platformCache = { name: '', tagline: '', logoUrl: '', color: '', loginDesign: '', loginImageUrl: '', loginImagePlacement: '' };
    } finally {
      platformPromise = null;
      subscribers.forEach((fn) => fn(platformCache));
    }
    return platformCache;
  })();
  return platformPromise;
}

/** Exposed so the sultan Platform Branding page can refresh after Save. */
export function invalidatePlatformBranding() {
  platformCache = null;
  platformPromise = null;
  fetchPlatformBranding();
}

export default function useBranding() {
  const orgSettings = useSelector((state) => state.org?.settings);
  const [platform, setPlatform] = useState(platformCache);

  useEffect(() => {
    if (!platformCache) fetchPlatformBranding();
    subscribers.add(setPlatform);
    return () => subscribers.delete(setPlatform);
  }, []);

  const p = platform || {};
  return {
    // false until the platform branding fetch has settled (success OR failure).
    // Consumers that WRITE the identity somewhere durable (tab title, favicon,
    // the cold-load cache) must wait for it — otherwise the theme default
    // below leaks out as "the brand" for the first few hundred ms on every
    // partner box, and gets cached as such.
    ready: platform !== null,
    name: orgSettings?.brandName || p.name || themeConfig.brand.name,
    tagline: orgSettings?.brandTagline || p.tagline || themeConfig.brand.tagline,
    logoUrl: orgSettings?.brandLogoUrl || p.logoUrl || themeConfig.brand.logoUrl || '',
    color: orgSettings?.brandColor || p.color || themeConfig.colors.brand,
    // Login-page layout — platform-only (no org tier: the login page renders
    // before any org context exists). Consumers normalize the raw value.
    loginDesign: p.loginDesign || '',
    loginImageUrl: p.loginImageUrl || '',
    loginImagePlacement: p.loginImagePlacement || '',
  };
}

/**
 * Returns only the platform override (without org layer or themeConfig
 * fallback). Used by the sultan branding form to prefill the fields with
 * what's currently saved, and by Organization branding placeholders so
 * org admins see the platform default as a suggestion.
 */
export function usePlatformBranding() {
  const [platform, setPlatform] = useState(platformCache);
  useEffect(() => {
    if (!platformCache) fetchPlatformBranding();
    subscribers.add(setPlatform);
    return () => subscribers.delete(setPlatform);
  }, []);
  return platform || { name: '', tagline: '', logoUrl: '', color: '', loginDesign: '', loginImageUrl: '', loginImagePlacement: '' };
}
